import type {
  AxiosError,
  AxiosRequestConfig,
  AxiosRequestHeaders,
  AxiosResponse,
  AxiosResponseHeaders
} from "axios";

export type AxiosSentryOptions = {
  tags: AxiosErrorContext;
  extra: Partial<{
    axios: Omit<AxiosErrorContext, "request_id">;
    additional_data: Record<string, any>;
  }>;
};

export type AxiosErrorContext = Partial<{
  method: string;
  url: string | undefined;
  request_id: string | undefined;
  requestHeaders: AxiosRequestHeaders | undefined;
  requestPayload: unknown;
  status: number;
  statusText: string;
  responseHeaders: AxiosResponseHeaders;
  responsePayload: unknown;
}>;

const isAxiosError = (
  error: any
): error is AxiosError<{ error?: { details?: string } }> => {
  return "isAxiosError" in error;
};

function getAxiosErrorIfAny(error: Error): AxiosError | null {
  if (isAxiosError(error)) {
    return error;
  }

  if (error.cause instanceof Error) {
    return getAxiosErrorIfAny(error.cause);
  }

  return null;
}

const getAxiosContextFromRequestConfig = (
  config: AxiosRequestConfig
): AxiosErrorContext => {
  const { url, headers, method, data } = config;
  return {
    method: method ? method.toUpperCase() : "",
    url,
    requestHeaders: headers,
    requestPayload: data
  };
};

const getAxiosContextFromResponse = (
  response: AxiosResponse
): AxiosErrorContext => {
  const { status, statusText, headers, data } = response;
  return {
    status,
    statusText,
    request_id: headers ? String(headers["x-request-id"]) : undefined,
    responseHeaders: headers,
    responsePayload: data
  };
};

const getAxiosErrorContext = (error: AxiosError): AxiosErrorContext => {
  if (error.response) {
    return {
      ...getAxiosContextFromRequestConfig(error.response.config),
      ...getAxiosContextFromResponse(error.response)
    };
  }

  if (error.config) {
    return {
      ...getAxiosContextFromRequestConfig(error.config)
    };
  }

  return {};
};

export function getAxiosOptions(
  error: Error | AxiosError
): AxiosSentryOptions | null {
  const axios_error = getAxiosErrorIfAny(error);

  if (!axios_error) {
    return null;
  }

  const { request_id, ...axios_context } = getAxiosErrorContext(axios_error);

  return {
    extra: { axios: axios_context },
    tags: { request_id }
  };
}
